import classNames from 'classnames'
import debounce from 'lodash/debounce.js'

import {showImporterOfRecordModal} from '../Modals/ImporterOfRecordModal.js'
import {
  updateLabelConfig,
  saveLabelInfo,
  attachCustomsInfoSelector,
  labelConfigSelector,
  importerOfRecordErrorsSelector,
  onLabelPropertyValueChange,
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
  isLabelPropertyEnabledSelector,
} from '../../../data/labelInfos/index.js'
import {getState, useSelector} from '../../../store.js'
import EnableLabelProperty from '../Fields/EnableLabelProperty.js'
import {FEDEX} from '../../../common/constants/ShipperNames.js'
import useLabelProperty from '../Fields/useLabelProperty.js'
import {useLabelConfigContext} from '../LabelConfigContext.js'
import className from '../../../common/className.js'
import {onCheckboxLabelPropertyEnable} from '../Fields/ConfigCheckbox.js'

const debouncedSaveLabelInfo = debounce(saveLabelInfo, 500)

export const LABEL_PROPERTY_INCLUDE_IMPORTER_OF_RECORD =
  'include_importer_of_record'
export const LABEL_PROPERTY_IMPORTER_OF_RECORD = 'importer_of_record'
const VALID_INCLUDE_IMPORTER_OF_RECORD_LABEL_PROPERTIES = [
  LABEL_PROPERTY_INCLUDE_IMPORTER_OF_RECORD,
]
const VALID_IMPORTER_OF_RECORD_LABEL_PROPERTIES = [
  LABEL_PROPERTY_IMPORTER_OF_RECORD,
]
const VALID_SHIPPER_TYPES = [FEDEX]

function includeImporterOfRecordLabelPropertiesFunc(shipperType, labelInfoID) {
  const attachCustomsInfo = attachCustomsInfoSelector(getState(), {labelInfoID})

  return attachCustomsInfo ? [LABEL_PROPERTY_INCLUDE_IMPORTER_OF_RECORD] : []
}

const INCLUDE_IMPORTER_OF_RECORD_DEPENDENT_PROPERTIES = [
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
]

function includeImporterOfRecordDependentPropertiesFunc() {
  return INCLUDE_IMPORTER_OF_RECORD_DEPENDENT_PROPERTIES
}

function importerOfRecordLabelPropertiesFunc(shipperType, labelInfoID) {
  const attachCustomsInfo = attachCustomsInfoSelector(getState(), {labelInfoID})
  const labelConfig = labelConfigSelector(getState(), {labelInfoID})

  return attachCustomsInfo &&
    labelConfig[LABEL_PROPERTY_INCLUDE_IMPORTER_OF_RECORD]
    ? [LABEL_PROPERTY_IMPORTER_OF_RECORD]
    : []
}

const IMPORTER_OF_RECORD_DEPENDENT_PROPERTIES = [
  LABEL_PROPERTY_ATTACH_CUSTOMS_INFO,
  LABEL_PROPERTY_INCLUDE_IMPORTER_OF_RECORD,
]

function importerOfRecordDependentPropertiesFunc() {
  return IMPORTER_OF_RECORD_DEPENDENT_PROPERTIES
}

export function IncludeImporterOfRecord() {
  const {labelInfoID, shipperType, isLabelEnableMode, isSummary} =
    useLabelConfigContext()
  const [includeImporterPropertySpecs] =
    IncludeImporterOfRecord.propertySpecList
  const [includeImporterLabelProperty] =
    includeImporterPropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)

  const attachCustomsInfo = useSelector(
    (state) => attachCustomsInfoSelector(state, {labelInfoID}) || false,
  )
  const {include_importer_of_record = false} = useSelector(
    (state) =>
      labelConfigSelector(state, {labelInfoID}) || {
        include_importer_of_record: false,
      },
  )
  const errors = useSelector((state) =>
    importerOfRecordErrorsSelector(state, {labelInfoID, shipperType}),
  )

  // include importer of record
  useLabelProperty({
    labelProperty: includeImporterLabelProperty,
    propertySpecs: includeImporterPropertySpecs,
  })

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: includeImporterLabelProperty,
    }),
  )

  const label = 'Importer of Record is NOT Recipient'

  if (!attachCustomsInfo || !VALID_SHIPPER_TYPES.includes(shipperType)) {
    return null
  }

  if (isSummary) {
    return label
  }

  const hasErrors = Object.keys(errors).length > 0
  const disabled = isLabelEnableMode && !isLabelPropertyEnabled

  return (
    <li
      className={className`list__item--shipping-options margin-bottom-15 ${{'property-disabled': disabled}}`}
    >
      <div className="wrap--edit-preset-form-input">
        <label className="inline-block margin-right-5 margin-bottom-0">
          <input
            type="checkbox"
            className="margin-right-5"
            checked={!!include_importer_of_record}
            onChange={(event) => {
              updateLabelConfig(labelInfoID, {
                [includeImporterLabelProperty]: event.target.checked,
              })

              onLabelPropertyValueChange(
                labelInfoID,
                includeImporterLabelProperty,
              )

              debouncedSaveLabelInfo(labelInfoID)
            }}
            disabled={disabled}
          />
          <span className="label--config__txt v-align-middle">{label}</span>
        </label>
        {include_importer_of_record && (
          <button
            className={classNames(
              'btn btn--link inline-block fs-n1 v-align-base',
              {error: hasErrors},
            )}
            type="button"
            onClick={() => showImporterOfRecordModal(labelInfoID, shipperType)}
            disabled={disabled}
          >
            Edit
          </button>
        )}
      </div>
      <EnableLabelProperty labelProperty={includeImporterLabelProperty} />
    </li>
  )
}

IncludeImporterOfRecord.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc: includeImporterOfRecordLabelPropertiesFunc,
    dependentPropertiesFunc: includeImporterOfRecordDependentPropertiesFunc,
    validLabelPropertiesFunc() {
      return VALID_INCLUDE_IMPORTER_OF_RECORD_LABEL_PROPERTIES
    },
    onLabelPropertyEnable: onCheckboxLabelPropertyEnable,
  },
]

export function ImporterOfRecord() {
  const {labelInfoID, shipperType, isLabelEnableMode, isSummary} =
    useLabelConfigContext()
  const [includeImporterPropertySpecs] =
    IncludeImporterOfRecord.propertySpecList
  const [importerPropertySpecs] = ImporterOfRecord.propertySpecList
  const [includeImporterLabelProperty] =
    includeImporterPropertySpecs.labelPropertiesFunc(shipperType, labelInfoID)
  const [importerLabelProperty] = importerPropertySpecs.labelPropertiesFunc(
    shipperType,
    labelInfoID,
  )

  const attachCustomsInfo = useSelector(
    (state) => attachCustomsInfoSelector(state, {labelInfoID}) || false,
  )
  const {include_importer_of_record = false, importer_of_record} = useSelector(
    (state) =>
      labelConfigSelector(state, {labelInfoID}) || {
        include_importer_of_record: false,
      },
  )

  const isLabelPropertyEnabled = useSelector((state) =>
    isLabelPropertyEnabledSelector(state, {
      labelInfoID,
      labelProperty: includeImporterLabelProperty,
    }),
  )

  // importer of record
  useLabelProperty({
    labelProperty: importerLabelProperty,
    propertySpecs: importerPropertySpecs,
  })

  if (
    !attachCustomsInfo ||
    !VALID_SHIPPER_TYPES.includes(shipperType) ||
    !(isLabelEnableMode && isLabelPropertyEnabled && include_importer_of_record)
  ) {
    return null
  }

  if (isSummary) {
    return 'Importer of Record'
  }

  return (
    <li className="list__item--shipping-options margin-bottom-15">
      <div>
        {(importer_of_record &&
          `${
            importer_of_record.address.company ||
            importer_of_record.address.name ||
            ''
          }${
            importer_of_record.tin.number
              ? ` (Tax ID#: ${importer_of_record.tin.number})`
              : ''
          }`) ||
          'Not Setup'}
      </div>
      <EnableLabelProperty labelProperty={importerLabelProperty} />
    </li>
  )
}

ImporterOfRecord.propertySpecList = [
  {
    validShipperTypes: VALID_SHIPPER_TYPES,
    labelPropertiesFunc: importerOfRecordLabelPropertiesFunc,
    dependentPropertiesFunc: importerOfRecordDependentPropertiesFunc,
    validLabelPropertiesFunc() {
      return VALID_IMPORTER_OF_RECORD_LABEL_PROPERTIES
    },
  },
]
